import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import type { AdminTraderItem } from '@pnl/shared';
import { api } from '../lib/api';
import { useAuthStore } from '../auth/auth.store';

const userStatusTone: Record<
  AdminTraderItem['userStatus'],
  'success' | 'neutral' | 'danger' | 'warning'
> = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
  BLOCKED: 'danger',
  PENDING_VERIFICATION: 'warning',
};

const userStatusLabel: Record<AdminTraderItem['userStatus'], string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  BLOCKED: 'Bloqueado',
  PENDING_VERIFICATION: 'Pendente',
};

export function TradersPage() {
  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">Operação</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Traders
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Traders ativos por mercado, com contas e status. Bloquear impede o login do trader.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <TradersCard marketCode="B3" title="Traders B3" />
        <TradersCard marketCode="FOREX" title="Traders Forex" />
      </div>
    </div>
  );
}

function TradersCard({ marketCode, title }: { marketCode: 'B3' | 'FOREX'; title: string }) {
  const qc = useQueryClient();
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const canBlock = useAuthStore((s) => s.hasPermission('users:write'));

  const query = useQuery({
    queryKey: ['admin', 'traders', { q, marketCode }],
    queryFn: () => api.listTraders({ ...(q ? { q } : {}), marketCode }),
  });
  const items = query.data?.items ?? [];

  const toggleBlock = useMutation({
    mutationFn: (t: AdminTraderItem) =>
      api.updateUser(t.userId, { status: t.userStatus === 'BLOCKED' ? 'ACTIVE' : 'BLOCKED' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'traders'] });
      setError(null);
    },
    onError: (e: unknown) =>
      setError(e instanceof PnlApiError ? e.api.message : 'Falha ao atualizar o trader.'),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>
              {query.data?.total ?? 0} trader(s){query.isLoading ? '…' : ''}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setQ(term.trim());
              }}
              placeholder="Buscar por nome ou e-mail…"
              className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/30 w-48 rounded-lg bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2"
            />
            <Button size="sm" variant="secondary" onClick={() => setQ(term.trim())}>
              Buscar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {error && (
          <div className="mb-4">
            <Alert tone="danger">{error}</Alert>
          </div>
        )}
        {query.isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Users size={28} />}
            title="Nenhum trader encontrado"
            description="Traders aparecem aqui após a ativação de um plano."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Trader</TH>
                <TH className="text-right">Contas</TH>
                <TH>Status</TH>
                <TH>Entrou em</TH>
                <TH className="text-right">Ações</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((t) => (
                <TR key={t.id}>
                  <TD>
                    <div className="text-ink-900 text-sm font-semibold">
                      {t.fullName || t.email}
                    </div>
                    <div className="text-ink-500 text-xs">{t.email}</div>
                  </TD>
                  <TD className="text-right tabular-nums">{t.accountsCount}</TD>
                  <TD>
                    <Badge tone={userStatusTone[t.userStatus]}>
                      {userStatusLabel[t.userStatus]}
                    </Badge>
                  </TD>
                  <TD>
                    <span className="text-ink-600 text-xs tabular-nums">
                      {new Date(t.joinedAt).toLocaleDateString('pt-BR')}
                    </span>
                  </TD>
                  <TD className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/traders/${t.userId}`}>
                        <Button size="sm" variant="ghost">
                          Ver tudo
                        </Button>
                      </Link>
                      {canBlock && (
                        <Button
                          size="sm"
                          variant={t.userStatus === 'BLOCKED' ? 'secondary' : 'danger'}
                          loading={toggleBlock.isPending && toggleBlock.variables?.id === t.id}
                          onClick={() => toggleBlock.mutate(t)}
                        >
                          {t.userStatus === 'BLOCKED' ? 'Desbloquear' : 'Bloquear'}
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}
